(() => {
  if (customElements.get('us-collection-buy')) return;
  const products = new Map();
  let hoveredFigure;
  const clearHover = () => {
    hoveredFigure?.removeAttribute('data-quick-hover');
    hoveredFigure = null;
  };
  const updateHover = event => {
    document.documentElement.removeAttribute('data-quick-keyboard');
    const figure = event.target instanceof Element ? event.target.closest('.us-collection-card .product-card__figure') : null;
    if (figure === hoveredFigure) return;
    clearHover();
    if (figure) { hoveredFigure = figure; figure.setAttribute('data-quick-hover', ''); }
  };
  document.addEventListener('pointermove', updateHover, { passive: true });
  document.addEventListener('pointerover', updateHover, { passive: true });
  document.addEventListener('pointerout', event => { if (!event.relatedTarget) clearHover(); });
  document.addEventListener('keydown', event => {
    if (event.key === 'Tab') document.documentElement.setAttribute('data-quick-keyboard', '');
  });
  window.addEventListener('blur', clearHover);
  const root = element => (element.dataset.root || '/').replace(/\/$/, '') + '/';
  function label(title, unit) {
    const parts = String(title).split('/').map(part => part.trim());
    const match = parts.find(part => new RegExp('^' + unit + '\\s*', 'i').test(part));
    return match ? [match, ...parts.filter(part => !/^(UK|EU|US)\s*\d/i.test(part))].join(' / ') : title;
  }
  class CollectionBuy extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.card = this.closest('product-card');
      this.card.classList.add('us-buy-ready');
      this.form = this.querySelector('form');
      this.select = this.querySelector('select');
      this.button = this.querySelector('[data-us-purchase]');
      this.status = this.querySelector('[role=status]');
      this.quick = this.card.querySelector('.us-collection-quick-look');
      this.card.addEventListener('pointerenter', () => this.load().catch(() => {}));
      this.card.addEventListener('focusin', () => this.load().catch(() => {}));
      this.quick.addEventListener('click', () => this.openPreview());
      this.form.addEventListener('submit', event => { event.preventDefault(); this.add(this.form); });
    }
    async load() {
      if (this.product) return this.product;
      if (this.loading) return this.loading;
      this.status.textContent = this.dataset.loading;
      this.loading = (async () => {
        const handle = this.dataset.handle;
        if (!products.has(handle)) products.set(handle, this.fetchProduct().catch(error => { products.delete(handle); throw error; }));
        const product = await products.get(handle);
        if (!product.variants?.length) throw Error('No variants');
        this.product = product;
        this.fill(this.form, this.dataset.unit);
        this.status.textContent = '';
        return product;
      })().catch(error => { this.status.textContent = this.dataset.error; throw error; }).finally(() => { this.loading = null; });
      return this.loading;
    }
    async fetchProduct() {
      const url = root(this) + 'products/' + encodeURIComponent(this.dataset.handle);
      const responses = await Promise.all([fetch(url + '.js'), fetch(url)]);
      if (responses.some(response => !response.ok)) throw Error(this.dataset.error);
      const product = await responses[0].json();
      const doc = new DOMParser().parseFromString(await responses[1].text(), 'text/html');
      const guide = [...doc.querySelectorAll('x-modal')].find(modal => /size|fit/i.test(modal.querySelector('[slot=header]')?.textContent || modal.id) && modal.querySelector('table'));
      if (guide) {
        const content = (guide.querySelector('.prose') || guide).cloneNode(true);
        content.querySelectorAll('script,style,iframe,form,[slot=header]').forEach(node => node.remove());
        content.querySelectorAll('*').forEach(node => [...node.attributes].filter(attribute => /^on/i.test(attribute.name)).forEach(attribute => node.removeAttribute(attribute.name)));
        product.sizeChart = { title: guide.querySelector('[slot=header]')?.textContent.trim() || this.dataset.sizeChart, html: content.innerHTML };
      }
      const embed = doc.querySelector('essential-preorder-app-embed');
      if (!embed) throw Error(this.dataset.error);
      const read = (name, fallback) => JSON.parse(embed.getAttribute(name) || JSON.stringify(fallback));
      product.essential = {
        campaigns: read('preorder-app-data', {}).preorderCampaigns || [],
        restockCampaigns: read('preorder-app-data-back-in-stock', {}).backInStockCampaigns || [],
        collections: read('product-collections', []),
        quantities: read('inventory-quantity-by-variant-id', {}),
        product: read('product', {}),
        market: window.essentialPreorderConfigs?.selectedMarketId || ''
      };
      return product;
    }
    availability(variant) {
      const sold = { kind: 'sold', text: this.dataset.sold, purchasable: false };
      if (!variant?.available) return sold;
      const data = this.product.essential;
      const source = data.product.variants?.find(item => item.id === variant.id);
      const plans = source?.selling_plan_allocations?.map(item => item.selling_plan_id) || [];
      const matches = campaign => (campaign.placement?.specificProducts || []).some(id => String(id).split('/').pop() === String(variant.id)) || campaign.sellingPlanIds?.some(id => plans.includes(id));
      const campaign = data.campaigns.find(item => matches(item) && item.content.marketType === 'SPECIFIC' && item.content.specificMarkets?.some(market => market.shopifyId.split('/').pop() === data.market)) || data.campaigns.find(item => matches(item) && (!item.content.marketType || item.content.marketType === 'ALL'));
      const quantity = data.quantities[variant.id];
      if (typeof quantity !== 'number') return { kind: 'preorder', text: this.dataset.details, purchasable: true, supported: false };
      if (quantity > 0 && (!campaign || campaign.content.preorderType === 'OUT_OF_STOCK')) return { kind: 'stock', text: '', purchasable: true };
      if (!campaign) return source?.inventory_management == null ? { kind: 'stock', text: '', purchasable: true } : sold;
      const content = campaign.content;
      if (content.preorderType === 'IN_STOCK' && quantity <= 0) return sold;
      const translated = campaign.contentTranslations?.find(item => item.locale === document.documentElement.lang);
      const value = key => translated?.[key] || content[key] || '';
      const plain = html => new DOMParser().parseFromString(html, 'text/html').body.textContent.trim();
      const message = plain(value('message'));
      const supported = !data.product.requires_selling_plan && content.paymentType === 'FULL' && !Number(content.discountValue) && !content.inventoryHasLimit && (!content.fulfilmentType || content.fulfilmentType === 'UNFULFILLED') && (!content.mixedCartType || content.mixedCartType === 'ONE_FULFILMENT');
      const properties = { _essential_preorder_campaign_id: campaign.id };
      const noteKey = value('cartNoteKey');
      if (noteKey && value('cartNoteText')) {
        properties[noteKey] = plain(value('cartNoteText'));
        properties._essential_preorder_props = encodeURIComponent(noteKey);
      }
      return { kind: 'preorder', text: [this.dataset.preorder.replace(/[.]$/, ''), message].filter(Boolean).join('. '), purchasable: true, supported, properties, message };
    }
    restockCampaign(variant) {
      if (!variant || variant.available) return null;
      const data = this.product.essential;
      const id = value => String(value).split('/').pop();
      const campaigns = data.restockCampaigns || [];
      const campaign = campaigns.find(item => item.assignment.specificProducts?.some(value => id(value) === String(variant.id))) || campaigns.find(item => item.assignment.specificCollections?.some(value => data.collections.some(collection => id(collection.id) === id(value.id)))) || campaigns.find(item => item.assignment.type === 'ALL_PRODUCTS');
      return campaign?.signal.notifyViaEmail !== false ? campaign : null;
    }
    openNotification(variant, trigger) {
      const campaign = this.restockCampaign(variant);
      if (!campaign) return;
      const translation = campaign.popUpTranslations?.find(item => item.locale === document.documentElement.lang);
      const plain = html => new DOMParser().parseFromString(html || '', 'text/html').body.textContent.trim();
      const text = key => plain(translation?.[key] || campaign.popUp[key] || '').replaceAll('{product}', this.product.title + ' — ' + variant.title);
      const modal = document.createElement('dialog'); modal.className = 'us-collection-notify'; modal.setAttribute('aria-label', text('title'));
      const close = document.createElement('button'); close.type = 'button'; close.className = 'us-collection-preview-close'; close.textContent = '×'; close.setAttribute('aria-label', this.dataset.close); close.onclick = () => window.usCloseDialog(modal);
      const heading = document.createElement('h2'); heading.className = 'h4'; heading.textContent = text('title');
      const description = document.createElement('p'); description.textContent = text('description');
      const form = document.createElement('form');
      const email = document.createElement('input'); email.type = 'email'; email.name = 'email'; email.required = true; email.autocomplete = 'email'; email.placeholder = text('emailFieldPlaceholder'); email.setAttribute('aria-label', text('emailFieldPlaceholder'));
      const submit = document.createElement('button'); submit.type = 'submit'; submit.className = 'button w-full'; submit.textContent = text('buttonText') || this.dataset.notify;
      const status = document.createElement('p'); status.setAttribute('role','status');
      const disclaimer = document.createElement('p'); disclaimer.className = 'us-collection-notify-disclaimer'; disclaimer.textContent = text('disclaimer');
      const photo = document.createElement('img');
      photo.className = 'us-collection-notify-image'; photo.alt = this.product.title;
      photo.width = 600; photo.height = 600;
      const photoSource = variant.featured_image?.src || this.product.featured_image;
      if (photoSource) {
        photo.src = photoSource;
        photo.addEventListener('error', () => { photo.hidden = true; }, { once: true });
      } else photo.hidden = true;
      form.append(email,submit); modal.append(close,heading,photo,description,form,status,disclaimer);
      form.onsubmit = async event => {
        event.preventDefault(); if (!form.reportValidity() || submit.disabled) return;
        submit.disabled = true; submit.setAttribute('aria-busy','true'); status.textContent = '';
        try {
          const response = await fetch('/apps/essential-preorder/notifications', {method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({email:email.value.trim(),variantDisplayName:this.product.title + ' - ' + variant.title,variantId:String(variant.id),variantUrl:new URL(root(this)+'products/'+this.dataset.handle+'?variant='+variant.id,location.origin).href,productId:this.product.id,marketId:this.product.essential.market,customerLocale:window.essentialPreorderConfigs?.selectedLocale || document.documentElement.lang || 'en',newsletterConsent:false})});
          const result = await response.json(); if (!response.ok || result.error) throw Error(result.error || this.dataset.error);
          heading.textContent = text('successTitle'); description.textContent = text('successDescription'); form.hidden = true; disclaimer.hidden = true;
        } catch(error) { status.textContent = error.message || this.dataset.error; }
        finally {submit.disabled = false;submit.removeAttribute('aria-busy');}
      };
      modal.addEventListener('close',()=>{modal.remove();trigger.focus({preventScroll:true});},{once:true}); document.body.append(modal);modal.showModal();
    }
    fill(form, unit, selected = '') {
      const select = form.querySelector('select');
      const button = form.querySelector('[data-us-purchase]');
      select.replaceChildren();
      const placeholder = new Option(this.dataset.size, '');
      placeholder.disabled = true;
      select.append(placeholder);
      this.product.variants.forEach(variant => {
        const state = this.availability(variant);
        const option = new Option(label(variant.title, unit) + ' ' + state.text, String(variant.id));
        option.disabled = !state.purchasable && !this.restockCampaign(variant);
        select.append(option);
      });
      const simple = this.product.variants.length === 1 && this.product.variants[0].title === 'Default Title';
      select.hidden = simple;
      select.disabled = false;
      select.value = selected || (simple && this.product.variants[0].available ? String(this.product.variants[0].id) : '');
      let menu;
      const update = () => {
        const variant = this.product.variants.find(value => String(value.id) === select.value);
        const state = this.availability(variant);
        const notify = Boolean(this.restockCampaign(variant));
        button.disabled = !state.purchasable && !notify;
        button.dataset.purchaseState = notify ? 'notify' : state.kind;
        button.hidden = !variant || (!state.purchasable && !notify);
        button.style.setProperty('display', button.hidden ? 'none' : 'block', 'important');
        button.style.setProperty('visibility', button.hidden ? 'hidden' : 'visible', 'important');
        form.dataset.purchaseReady = String(Boolean(variant && (state.purchasable || notify)));
        button.textContent = notify ? this.dataset.notify : state.kind === 'preorder' ? (state.supported ? this.dataset.preorder.replace(/pre-order/i, 'Preorder') : this.dataset.details) : this.dataset.add;
        form.querySelector('[role=status]').textContent = variant ? (state.kind === 'preorder' && state.supported ? state.message : state.text) : '';
        form.querySelector('[role=status]').dataset.purchaseState = state.kind;
        if (menu) menu.querySelectorAll('[data-variant-id]').forEach(choice => choice.setAttribute('aria-pressed', String(choice.dataset.variantId === select.value)));
        if (form.dataset.preview !== undefined && variant) {
          const price = this.dialog?.querySelector('[data-preview-price]');
          if (price) price.textContent = this.money(variant.price);
        }
      };
      select.onchange = update;
      button.textContent = this.product.available === false ? this.dataset.sold : this.dataset.add;
      update();
      form.querySelector('.us-size-menu')?.remove();
      select.classList.toggle('us-size-native', !simple);
      if (!simple) {
        menu = document.createElement('div');
        menu.className = 'us-size-menu';
        if (form.dataset.preview === undefined) {
          const units = document.createElement('div');
          units.className = 'us-collection-preview-units';
          units.setAttribute('role', 'group');
          units.setAttribute('aria-label', this.dataset.system);
          const available = ['UK','EU','US'].filter(system => this.product.variants.every(variant => new RegExp('(?:^|/)\\s*' + system + '\\s*\\d', 'i').test(variant.title)));
          available.forEach(system => {
            const tab = document.createElement('button');
            tab.type = 'button';
            tab.textContent = system;
            tab.setAttribute('aria-pressed', String(system === unit));
            tab.onclick = () => this.fill(form, system, select.value);
            units.append(tab);
          });
          if (available.length > 1) menu.append(units);
        }
        const options = document.createElement('div');
        options.className = 'us-size-grid';
        options.setAttribute('role', 'group');
        options.setAttribute('aria-label', this.dataset.size);
        this.product.variants.forEach(variant => {
          const state = this.availability(variant);
          const choice = document.createElement('button');
          choice.type = 'button';
          choice.disabled = !state.purchasable && !this.restockCampaign(variant);
          choice.dataset.variantId = String(variant.id);
          choice.dataset.availability = state.kind;
          choice.setAttribute('aria-pressed', String(select.value === String(variant.id)));
          choice.setAttribute('aria-label', [label(variant.title, unit), state.text].filter(Boolean).join('. '));
          const size = document.createElement('span');
          size.textContent = label(variant.title, unit).replace(/^(UK|US|EU)\s*/i, '');
          choice.append(size);
          if (this.restockCampaign(variant)) {
            choice.classList.add('us-size-notify');
            choice.setAttribute('aria-label', [label(variant.title, unit), this.dataset.sold, this.dataset.notify].join('. '));
            const bell = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            bell.setAttribute('viewBox', '0 0 24 24'); bell.setAttribute('aria-hidden', 'true'); bell.setAttribute('fill', 'none'); bell.setAttribute('stroke', 'currentColor'); bell.setAttribute('stroke-width', '1.5'); bell.setAttribute('stroke-linecap', 'round'); bell.setAttribute('stroke-linejoin', 'round'); bell.classList.add('us-size-notify-bell');
            const path = document.createElementNS('http://www.w3.org/2000/svg', 'path'); path.setAttribute('d', 'M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M10 21h4'); bell.append(path); choice.append(bell);
          }

          choice.addEventListener('click', () => {
            select.value = String(variant.id);
            update();
          });
          options.append(choice);
        });
        menu.append(options);
        select.after(menu);
      }
    }
    money(price) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', { style: 'currency', currency: window.Shopify?.currency?.active || this.dataset.currency || 'GBP' }).format(price / 100);
    }
    async add(form) {
      if (form.dataset.busy) return;
      const variant = this.product?.variants.find(value => String(value.id) === form.querySelector('select').value);
      if (this.restockCampaign(variant)) { this.openNotification(variant, form.querySelector('[data-us-purchase]')); return; }
      if (!variant?.available) { form.querySelector('select').focus(); return; }
      const status = form.querySelector('[role=status]');
      const button = form.querySelector('[data-us-purchase]');
      form.dataset.busy = 'true';
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      let added = false;
      try {
        this.product = await this.fetchProduct();
        const current = this.product.variants.find(item => item.id === variant.id);
        const state = this.availability(current);
        if (!state.purchasable) throw Error(this.dataset.sold);
        if (state.kind === 'preorder' && !state.supported) {
          location.assign(root(this) + 'products/' + this.dataset.handle + '?variant=' + variant.id);
          return;
        }
        const item = { id: variant.id, quantity: 1 };
        if (state.kind === 'preorder') item.properties = state.properties;
        const sections = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
        const response = await fetch(root(this) + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items: [item], sections: [...new Set(sections)].slice(0,5), sections_url: location.pathname })
        });
        const result = await response.json();
        if (!response.ok) throw Error(typeof result.description === 'string' ? result.description : this.dataset.error);
        added = true;
        status.textContent = this.dataset.added;
        const cartResponse = await fetch(root(this) + 'cart.js');
        if (!cartResponse.ok) throw Error('Cart refresh failed');
        const cart = await cartResponse.json();
        cart.sections = result.sections || {};
        if (sections.some(section => !cart.sections[section])) {
          const sectionResponse = await fetch(root(this) + '?sections=' + encodeURIComponent([...new Set(sections)].join(',')));
          if (!sectionResponse.ok) throw Error('Cart refresh failed');
          cart.sections = await sectionResponse.json();
          if (sections.some(section => !cart.sections[section])) throw Error('Cart refresh failed');
        }
        if (this.dialog?.open) {
          const dialog = this.dialog;
          await new Promise(resolve => {
            dialog.addEventListener('close', resolve, { once: true });
            window.usCloseDialog(dialog);
          });
        }
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } }));
        const drawer = document.querySelector('cart-drawer');
        if (window.themeVariables?.settings?.cartType === 'page' || !drawer) location.assign(root(this) + 'cart');
        else if (typeof drawer.show === 'function') drawer.show();
      } catch (error) {
        status.textContent = added ? this.dataset.added : error.message || this.dataset.error;
        if (added) location.assign(root(this) + 'cart');
      } finally {
        delete form.dataset.busy;
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }
    async openPreview() {
      if (this.opening || this.dialog?.open) return;
      this.opening = true;
      clearHover();
      this.quick.setAttribute('aria-busy', 'true');
      try {
        await this.load();
        const dialog = document.createElement('dialog');
        this.dialog = dialog;
        dialog.className = 'us-collection-preview';
        dialog.setAttribute('aria-label', this.product.title);
        const close = document.createElement('button');
        close.type = 'button'; close.className = 'us-collection-preview-close'; close.textContent = '×'; close.setAttribute('aria-label', this.dataset.close);
        close.onclick = () => window.usCloseDialog(dialog);
        const grid = document.createElement('div'); grid.className = 'us-collection-preview-grid';
        const media = document.createElement('div'); media.className = 'us-collection-preview-media';
        const image = document.createElement('img'); image.alt = this.product.title; image.width = 500; image.height = 500;
        const images = this.product.images || [];
        let index = 0;
        const showImage = () => { if(images.length) image.src = images[index]; };
        showImage(); media.append(image);
        if (images.length > 1) [-1,1].forEach(direction => {
          const arrow = document.createElement('button'); arrow.type = 'button'; arrow.textContent = direction < 0 ? '‹' : '›'; arrow.setAttribute('aria-label', direction < 0 ? this.dataset.previous : this.dataset.next);
          arrow.onclick = () => { index = (index + direction + images.length) % images.length; showImage(); }; media.append(arrow);
        });
        const info = document.createElement('div'); info.className = 'us-collection-preview-info';
        const title = document.createElement('h2'); title.className = 'h3'; title.textContent = this.product.title;
        const price = document.createElement('p'); price.dataset.previewPrice = ''; price.textContent = this.money(this.product.price);
        const units = document.createElement('div'); units.className = 'us-collection-preview-units'; units.setAttribute('role','group'); units.setAttribute('aria-label', this.dataset.system);
        const form = document.createElement('form');
        form.className = 'us-collection-buy-form';
        form.dataset.preview = '';
        const sizeSelect = document.createElement('select');
        sizeSelect.required = true;
        sizeSelect.setAttribute('aria-label', this.dataset.size);
        const purchaseButton = document.createElement('button');
        purchaseButton.type = 'submit';
        purchaseButton.className = 'button w-full';
        purchaseButton.dataset.usPurchase = '';
        purchaseButton.hidden = true;
        const purchaseStatus = document.createElement('p');
        purchaseStatus.className = 'us-collection-buy-status';
        purchaseStatus.setAttribute('role', 'status');
        purchaseStatus.setAttribute('aria-live', 'polite');
        form.append(sizeSelect, purchaseButton, purchaseStatus);
        this.fill(form, this.dataset.unit, this.select.value);
        const available = ['UK','EU','US'].filter(unit => this.product.variants.every(variant => new RegExp('(?:^|/)\\s*'+unit+'\\s*\\d','i').test(variant.title)));
        available.forEach(unit => {
          const tab = document.createElement('button'); tab.type = 'button'; tab.textContent = unit; tab.setAttribute('aria-pressed',String(unit===this.dataset.unit));
          tab.onclick = () => { this.fill(form,unit,form.querySelector('select').value); units.querySelectorAll('button').forEach(button=>button.setAttribute('aria-pressed',String(button===tab))); };
          units.append(tab);
        });
        units.hidden = available.length < 2;
        form.addEventListener('submit',event=>{event.preventDefault();this.add(form)});
        const details = document.createElement('a'); details.href = root(this)+'products/'+this.dataset.handle; details.textContent = this.dataset.details;
        const footer = document.createElement('div'); footer.className = 'us-collection-preview-footer'; footer.append(details);
        if (this.product.sizeChart) {
          const chartLink = document.createElement('button'); chartLink.type = 'button'; chartLink.className = 'us-collection-size-chart-link'; chartLink.textContent = this.dataset.sizeChart;
          chartLink.onclick = () => {
            const chart = document.createElement('dialog'); chart.className = 'us-collection-size-chart'; chart.setAttribute('aria-label', this.product.sizeChart.title);
            const heading = document.createElement('h2'); heading.textContent = this.product.sizeChart.title;
            const chartClose = document.createElement('button'); chartClose.type = 'button'; chartClose.className = 'us-collection-preview-close'; chartClose.textContent = '×'; chartClose.setAttribute('aria-label', this.dataset.close); chartClose.onclick = () => window.usCloseDialog(chart);
            const content = document.createElement('div'); content.className = 'us-collection-size-chart-content'; content.innerHTML = this.product.sizeChart.html;
            content.querySelectorAll('[id]').forEach(node => node.removeAttribute('id'));
            chart.append(chartClose,heading,content); document.body.append(chart);
            chart.addEventListener('close', () => { chart.remove(); chartLink.focus({preventScroll:true}); }, {once:true}); chart.showModal();
          };
          footer.append(chartLink);
        }
        info.append(title,price,units,form,footer); grid.append(media,info); dialog.append(close,grid); document.body.append(dialog);
        const previousOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';
        this.card.classList.add('us-quick-open'); this.quick.setAttribute('aria-expanded','true');
        dialog.addEventListener('close',()=>{
          document.documentElement.style.overflow = previousOverflow;
          this.card.classList.remove('us-quick-open');this.quick.setAttribute('aria-expanded','false');this.quick.focus({preventScroll:true});dialog.remove();this.dialog=null;
        },{once:true});
        dialog.showModal();
      } catch { this.status.textContent = this.dataset.error; }
      finally { this.opening = false;this.quick.removeAttribute('aria-busy'); }
    }
  }
  document.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target : null;
    const link = target?.closest('.us-collection-selector__link[data-filter-param]');
    if (!link) return;
    const filterParam = link.dataset.filterParam;
    if (!filterParam) return;
    const url = new URL(link.getAttribute('href') || window.location.href, window.location.origin);
    url.searchParams.delete(filterParam);
    if (link.dataset.filterValue) url.searchParams.append(filterParam, link.dataset.filterValue);
    url.searchParams.delete('page');
    link.href = url.pathname + url.search + url.hash;
  }, true);
  customElements.define('us-collection-buy', CollectionBuy);
})();




(() => {
  const ready = new WeakSet();
  const resizeObserver = new ResizeObserver(entries => {
    for (const entry of entries) update(entry.target.closest('.us-collection-selector__scroll'));
  });
  function update(row) {
    if (!row) return;
    row.dataset.moreOptions = String(row.scrollWidth - row.clientWidth - Math.abs(row.scrollLeft) > 2);
  }
  function init() {
    document.querySelectorAll('.us-collection-selector__scroll').forEach(row => {
      if (ready.has(row)) return;
      ready.add(row);
      row.addEventListener('scroll', () => update(row), { passive: true });
      resizeObserver.observe(row);
      Array.from(row.children).forEach(link => resizeObserver.observe(link));
      update(row);
    });
  }
  let scheduled = false;
  new MutationObserver(() => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => { scheduled = false; init(); });
  }).observe(document.documentElement, { childList: true, subtree: true });
  init();
})();
